import { useId } from 'react'
import type { CourseWithoutBlocks, CourseUpdate } from '@/types/course'
import { Input, Label, Switch } from '@teach/ui'
import { trialLessonsLimit } from '../../lib/trialLimit'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  local: CourseWithoutBlocks
  setLocal: (next: CourseWithoutBlocks) => void
  save: (patch: CourseUpdate) => void
  isCourseAuthor: boolean
}

export const TrialAccessRow = ({ local, setLocal, save, isCourseAuthor }: Props) => {
  const trialId = useId()
  const countId = useId()
  const hintId = useId()
  const m = useMessages(messages)
  const disabledTitle = !isCourseAuthor ? m.noRights : undefined
  // Хотя бы один урок обязан остаться платным: при одном уроке (или без уроков)
  // бесплатных быть не может.
  const maxTrialLessons = trialLessonsLimit(local.lessons.length)
  const trialImpossible = maxTrialLessons < 1
  // Значение выше лимита не срезаем молча: показываем подсказку и не шлём. Бэк отказывает
  // только при росте, так что старое значение выше лимита остаётся как есть.
  const overLimit = local.trialLessonsCount > maxTrialLessons

  return (
    <div className="space-y-4 rounded-2xl bg-foreground/5 p-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Label htmlFor={trialId}>{m.trialAccess}</Label>
          <p className="mt-1 text-sm text-muted-foreground">
            {trialImpossible ? m.trialImpossible : m.trialHint(maxTrialLessons)}
          </p>
        </div>
        <Switch
          id={trialId}
          checked={local.trialLessonsCount > 0}
          onCheckedChange={(on) => {
            const trialLessonsCount = on ? 1 : 0
            setLocal({ ...local, trialLessonsCount })
            save({ trialLessonsCount })
          }}
          disabled={!isCourseAuthor || (trialImpossible && local.trialLessonsCount === 0)}
          title={disabledTitle}
        />
      </div>
      {local.trialLessonsCount > 0 && !trialImpossible ? (
        <div className="space-y-3 border-t border-foreground/10 pt-4">
          <Label htmlFor={countId} className="!text-base">
            {m.trialCount}
          </Label>
          <Input
            id={countId}
            type="number"
            min="1"
            value={local.trialLessonsCount}
            aria-invalid={overLimit || undefined}
            aria-describedby={overLimit ? hintId : undefined}
            onChange={(e) => {
              const trialLessonsCount = Math.max(1, Number(e.target.value) || 1)
              setLocal({ ...local, trialLessonsCount })
            }}
            onBlur={() => {
              if (overLimit) return
              save({ trialLessonsCount: local.trialLessonsCount })
            }}
            disabled={!isCourseAuthor}
            title={disabledTitle}
            className="h-11 w-32 text-base"
          />
          {overLimit ? (
            <p id={hintId} role="alert" className="text-sm text-destructive">
              {m.trialOverLimit(maxTrialLessons)}
            </p>
          ) : null}
          <p className="text-sm text-muted-foreground">{m.trialTotal(local.lessons.length)}</p>
        </div>
      ) : null}
    </div>
  )
}
